import { CircularProgress, Collapse, Divider, Grid, Box, Typography } from "@mui/material";
import LongCard from "components/ui/LongCard/LongCard";
import StockCard from "components/ui/StockCard";
import RemoveModal from "components/ui/RemoveModal/RemoveModal";
import DiamondQualityModal from "components/ui/DiamondQualityModal/DiamondQualityModal";
import { useGlobalContext } from "context/GlobalContext";
import { usePermissions } from "context/PermissionsContext";
import React, { useContext, useEffect, useRef, useState, useCallback } from "react";
import { findSpecificMenu, findSpecificPage, findSpecificSubMenu, hasAccessPage, isAllowed } from "utils/checkUserRigths";
import { transformDataForCreateOrderPage } from "utils/transformdata";
import { useLocation, useNavigate } from "react-router-dom";
import SortContext from "context/SortContext";
import { useFilter } from "context/FilterContext";
import { ReactComponent as ColorDiamond } from "../assets/orderClient/color_diamond.svg";
import { ReactComponent as ColorGold } from "../assets/orderClient/color_gold.svg";
import { ReactComponent as BlueDiamond } from "../assets/icons/blueDiamond.svg";
import { ReactComponent as ColorRings } from "../assets/orderClient/color_rings.svg";
import { ReactComponent as ColorCategory } from "../assets/orderClient/color_category.svg";
import { ReactComponent as DownArrow } from "../assets/icons/Arrowdown.svg";
import { ReactComponent as InfoIcon } from "../assets/icons/Bredcrum_Icon/infoIcon.svg";
import { useMemo } from "react";
import apiClient from "lib/api-client";
import useCategorySizes from "hooks/useCategorySizes";
import { useDispatch, useSelector } from "react-redux";
import { initCart, removeFromCart, removeFromMeltingCart } from "store/cartSlice";

import "features/Stock/StockProd/components/product.css";
import GoldPurityModal from "components/ui/GoldModal/GoldModal";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";

// Client Design (CD-xxxxx) styles have no Style record, so no category or sizes
const CLIENT_DESIGN_CATEGORY = "Client Design";
const NO_SIZES = []; // stable ref, LongCard effect depends on sizeData
const isClientDesign = (item) => /^CD-/i.test(item?.styleNumber || "");
const getCartCategoryName = (item) => item?.categoryName || (isClientDesign(item) ? CLIENT_DESIGN_CATEGORY : item?.categoryName);

function CartOrder() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [filteredData, setFilteredData] = useState([]);
  const [removeProductModal, setRemoveProductModal] = useState(false);
  const [productToRemove, setProductToRemove] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const permission = usePermissions();
  // const hasAccessViewCategory = hasAccessPage(findSpecificPage("Category", permission), "view");
  const hasAccessViewCategory = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Category Access"), "view");
  // const hasAccessViewCollection = hasAccessPage(findSpecificPage("Collection", permission), "view");
  const hasAccessViewCollection = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Collection Access"), "view");
  // const hasAccessViewSets = hasAccessPage(findSpecificPage("Sets", permission), "view");
  const hasAccessViewSets = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Sets Access"), "view");
  // const hasAccessViewStocks = hasAccessPage(findSpecificPage("Stock", permission), "view");
  const hasAccessViewStocks = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Stocks Access"), "view");
  const { search } = useContext(SortContext);

  const { setIsSetFilter, setBackendFilterValues, setFilterValues } = useFilter();
  const innerChnages = location?.state?.innerChnages;
  const isMeltingStock = location?.state?.isMeltingFlow || location?.state?.newName === "Create Melting Stock";
  const isCrmFlow = Boolean(location?.state?.crmSource);
  const crmFlowName = location?.state?.crmFlowName || "";
  const isAddQuoteFlow = isCrmFlow && crmFlowName !== "Create Order" && crmFlowName !== "Create Quote";

  const { setCreateBtn, setCreateBtnName, setCreateBtnOption, setCountCart, setClientDetails, setClientDetailsComp, setCartData, expanded, setExpanded } = useGlobalContext();
  const [invalidStyleNumbers, setInvalidStyleNumbers] = useState([]);
  const [showBtn, setShowBtn] = useState(true);

  // Separate modal states
  const [diamondModalOpen, setDiamondModalOpen] = useState(false);
  const [goldModalOpen, setGoldModalOpen] = useState(false);

  // Selector logic based on flow
  const cartData = useSelector((state) => state.cart.cartData);
  let leadDetails = null;

  useEffect(() => {
    if (location.state?.innerChnages) return;
    const params = new URLSearchParams(window.location.search);
    const encoded = params.get("crmState");
    if (!encoded) return;

    const init = async () => {
      try {
        const crmState = JSON.parse(atob(encoded));
        if (crmState?.leadId) {
          const res = await apiClient.get(`${process.env.REACT_APP_CRM_SERVER_URL}/api/v1/workflow/sales/getLeadFullDetailsByLeadId/${crmState.leadId}`);
          const result = res.data?.data || res.data;
          leadDetails = result;

          const cartItems = (result.summary?.styles || []).map((style) => ({
            ...style,
            styleNumber: style.styleNumber || style.styleCode,
            jewelCode: style.jewelCode || "", // stock lines carry jewelCode, design lines don't
            orderQty: style.quantity,
            goldQlty: style.goldKarat,
            goldQuality: style.goldKarat,
            goldColor: style.goldColor,
            diaQlty: style.diamondQuality,
            diamondQuality: style.diamondQuality,
            rhodium: style.rhodium,
            itemSize: style.size && style.size !== "NA" ? style.size : null,
            weight: `${style.netWeight ?? 0} g / ${style.grossWeight ?? 0} g`,
            diamond: `${style.diaWeight ?? 0} Ct / ${style.diaPcs ?? 0} Pcs`,
            img: style.transformedImageUrl || style.imageUrl || "",
            Orgimg: style.imageUrl || style.transformedImageUrl || "",
            isAddedInCart: true,
            cartItemId: uuidv4(), // unique per line, same design can repeat with different sizes
            itemPoNo: style.itemPONumber || "",
            message: style.remarks || "",
          }));
          sessionStorage.setItem("CartData", JSON.stringify(cartItems));
          dispatch(initCart());

          const certificateOptions = ["IGI", "DGLA", "SGL", "GIA", "GSI", "None"];
          const leadCertificate = result.certificate ?? result.summary?.certificate;
          const matchedCertificate = certificateOptions.find((c) => c.toLowerCase() === String(leadCertificate || "").trim().toLowerCase());

          const existingFormData = JSON.parse(sessionStorage.getItem("clientFormData") || "{}");
          sessionStorage.setItem("clientFormData", JSON.stringify({
            ...existingFormData,
            poNumber: result.orderPONumber || result.summary?.orderPONumber || existingFormData.poNumber || "",
            remarks: result.orderRemarks || result.summary?.orderRemarks || existingFormData.remarks || "",
            certificate: matchedCertificate || existingFormData.certificate || "",
            clientName: {
              name: result.clientName || "",
              partyNumber: null,
              partyCode: result.clientCode || "",
            },
          }));
        }
        navigate("/CartOrder", { replace: true, state: { ...crmState, leadDetails, newName: "Cart" } });
      } catch (_) { }
    };

    init();
  }, []);

  useEffect(() => {
    if (cartData?.length === 0) {
      const params = new URLSearchParams(window.location.search);
      if (params.get("crmState")) return; 
      navigate(-1);
    }
  }, [cartData]);

  // Get Diamond Quality Breakdown
  const getDiamondQualityBreakdown = useCallback(() => {
    if (!cartData || cartData.length === 0) return [];

    const counts = {};
    const diamondMasterList = ["VVS-EF", "VVS-FG", "VVS-GH", "VS-GH", "SI-HI", "SI-IJ"];

    // Initialize all master list items with 0
    diamondMasterList.forEach((key) => (counts[key] = 0));

    cartData.forEach((item) => {
      const quality = (item?.diaQlty || item?.diamondQuality || "-").replace(/(.+)(..)$/, "$1-$2");

      if (counts[quality] !== undefined) {
        counts[quality]++;
      }
    });

    return Object.entries(counts)
      .map(([key, value]) => ({ key, value }))
      .sort((a, b) => {
        const list = diamondMasterList;
        return list.indexOf(a.key) - list.indexOf(b.key);
      });
  }, [cartData]);

  // Get Gold Quality Breakdown
  const getGoldQualityBreakdown = useCallback(() => {
    if (!cartData || cartData.length === 0) return [];

    const counts = {};
    const goldMasterList = ["18 karat", "14 karat"];

    // Initialize all master list items with 0
    goldMasterList.forEach((key) => (counts[key] = 0));

    cartData.forEach((item) => {
      let quality = item?.goldQlty || item?.goldQuality || "";

      // Format the quality string
      if (quality.includes("K")) {
        quality = quality.replace("K", " karat");
      } else if (quality && !quality.includes("karat")) {
        quality = `${quality} karat`;
      }

      // Clean up any extra spaces
      quality = quality.trim();

      if (counts[quality] !== undefined) {
        counts[quality]++;
      } else {
        counts[quality] = 1;
      }
    });

    return (
      Object.entries(counts)
        .map(([key, value]) => ({ key, value }))
        // .filter(item => item.value > 0)
        .sort((a, b) => {
          const list = goldMasterList;
          const indexA = list.indexOf(a.key);
          const indexB = list.indexOf(b.key);

          if (indexA !== -1 && indexB !== -1) return indexA - indexB;
          if (indexA !== -1) return -1;
          if (indexB !== -1) return 1;

          return a.key.localeCompare(b.key);
        })
    );
  }, [cartData]);

  // Memoized quality data
  const diamondQualityData = useMemo(
    () =>
      getDiamondQualityBreakdown().map((item) => ({
        quality: item.key,
        count: item.value,
      })),
    [getDiamondQualityBreakdown]
  );

  const goldQualityData = useMemo(
    () =>
      getGoldQualityBreakdown().map((item) => ({
        purity: item.key,
        count: item.value,
      })),
    [getGoldQualityBreakdown]
  );

  // Initialize collapsed state
  const [collapsed, setCollapsed] = useState({});

  const categorySections = useMemo(() => {
    if (filteredData?.length > 0) {
      const data = new Map();
      for (let i = 0; i < filteredData.length; i++) {
        const currentData = filteredData[i];
        const categoryName = getCartCategoryName(currentData);
        if (!data.has(categoryName)) {
          data.set(categoryName, [currentData]);
        } else {
          data.get(categoryName).push(currentData);
        }
      }
      return [...data];
    }
    return [];
  }, [filteredData]);

  useEffect(() => {
    dispatch(initCart());
  }, [dispatch]);

  // Sync collapsed state with expanded from context
  useEffect(() => {
    if (categorySections.length > 0) {
      const newCollapsed = {};
      categorySections.forEach(([categoryName]) => {
        newCollapsed[categoryName] = !expanded;
      });
      setCollapsed(newCollapsed);
    }
  }, [expanded, categorySections]);

  // Check if all categories are collapsed or expanded
  useEffect(() => {
    if (categorySections.length > 0 && Object.keys(collapsed).length > 0) {
      const allCollapsed = Object.values(collapsed).every((val) => val);
      const allExpanded = Object.values(collapsed).every((val) => !val);

      if (allExpanded && !expanded) {
        setExpanded(true);
      } else if (allCollapsed && expanded) {
        setExpanded(false);
      }
    }
  }, [collapsed]);

  const handleToggleCategory = (categoryName) => {
    setCollapsed((prev) => ({
      ...prev,
      [categoryName]: !prev[categoryName],
    }));
  };

  const handlemodalOpen = (product) => {
    setProductToRemove(product);
    setRemoveProductModal(true);
  };

  const removeCart = () => {
    if (productToRemove) {
      if (isMeltingStock) {
        dispatch(removeFromMeltingCart(productToRemove));
      } else {
        dispatch(removeFromCart(productToRemove));
      }

      sessionStorage.setItem("RecentlyRemoved", JSON.stringify(productToRemove));
      window.dispatchEvent(new Event("cartUpdated"));
      setProductToRemove(null);
      setRemoveProductModal(false);
    }
  };

  const totalCalWeight = useCallback(
    (type = "diamond") => {
      const total = cartData?.reduce((total, item) => {
        let weightStr = "";

        if (type === "diamond") {
          weightStr = item?.diaWt || item?.diaWeight || item?.diamond?.split(" Ct /")[0] || "0";
        } else if (type === "gold") {
          weightStr = item?.netWeight || item?.netWt || item?.weight?.split(" g /")[0] || "0";
        }

        const weight = parseFloat(weightStr) || 0;
        return total + weight;
      }, 0);

      return total.toFixed(2);
    },
    [cartData]
  );

  // Memoized weight calculations
  const diamondWeight = useMemo(() => totalCalWeight("diamond"), [totalCalWeight]);
  const goldWeight = useMemo(() => totalCalWeight("gold"), [totalCalWeight]);

  const handleProceedClickWrapper = () => {
    const invalidItems = cartData.filter((item) => item?.sizeAvailable !== false && (!item.itemSize || item.itemSize === "Select" || item.itemSize === "Select Size"));

    const invalidStyleNumbers = invalidItems.map((item) => item.styleNumber);

    setInvalidStyleNumbers(invalidStyleNumbers);

    if (invalidStyleNumbers.length > 0) {
      const element = document.getElementById(`size-select-${invalidStyleNumbers[0]}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    } else {
      handleProceedClick();
    }
  };

  const handleUpdateClick = async () => {
    const orderNo = location.state?.OrderNo || JSON.parse(sessionStorage.getItem("crmState") || "{}")?.OrderNo;
    const styleDataList = cartData.map((item) => ({
      styleNumber: item.styleNumber,
      itemSize: item.itemSize || null,
      orderQty: item.orderQty || 1,
      goldQlty: item.goldQlty || item.goldQuality || null,
      goldColor: item.goldColor || null,
      rhodium: item.rhodium || null,
      itemPoNo: item.itemPoNo || null,
      designClass: item.designClass || null,
      message: item.message || null,
      diaQlty: item.diaQlty || item.diamondQuality || null,
    }));
    console.log({ OrderNo: orderNo, styleDataList })
    try {
      setUpdateLoading(true);
      await apiClient.post(`/api/v13/crm/addDesignToQuote`, { OrderNo: orderNo, styleDataList });
      window.location.replace(location.state?.backPath);
    } catch (error) {
      console.error("addDesignToQuote error:", error);
      toast.error(error?.response?.data?.message || "Failed to add designs to quote", {
        autoClose: 3000,
        hideProgressBar: false,
      });
    } finally {
      setUpdateLoading(false);
    }
  };

  // To Handle Proceed Button color
  useEffect(() => {
    if (!cartData || cartData.length === 0) {
      setShowBtn(true);
      return;
    }

    const isAnyMissingSizeAvailableKey = cartData.some((data) => data?.sizeAvailable === undefined);

    if (isAnyMissingSizeAvailableKey) {
      setShowBtn(true);
      return;
    }

    const hasInvalidDesign = cartData.some((data) => !data.itemSize && data.sizeAvailable);

    if (hasInvalidDesign) {
      setShowBtn(true);
    } else {
      setShowBtn(false);
    }
  }, [cartData]);

  const handleProceedClick = () => {
    const state = {
      ...location.state,
      innerChnages: true,
      openCart: false,
      newName: "Create Order",
      clientDetails: true,
    };

    navigate(`/Client-Details`, { state });
  };

  const btnInfo = [
    {
      btnName: "Remove",
      bgColor: "#E77B7B",
      click: removeCart,
    },
    {
      btnName: "Keep",
      bgColor: "#50C878",
      click: () => setRemoveProductModal(false),
    },
  ];

  // Filter data based on search
  useEffect(() => {
    if (search) {
      const data = cartData.filter((item) => {
        return item?.jewelCode?.toLowerCase()?.includes(search.toLowerCase()) || item?.styleNumber?.toLowerCase()?.includes(search.toLowerCase());
      });
      setFilteredData(data);
    } else {
      setFilteredData(cartData);
    }
  }, [search, cartData]);

  // Fetch filter data
  useEffect(() => {
    setIsSetFilter(true);
    const controller = new AbortController();

    const fetchFilterData = async () => {
      try {
        const response = await apiClient.get(`/api/v1/set/getSetDesignSlidersData`, {
          signal: controller.signal,
        });
        if (response.data && response.data.data) {
          const filterData = {
            "Diamond Quality": [],
            "Gold Purity": [],
            "Gold Color": [],
            Categories: [...new Set(cartData.map((item) => item.categoryName))] || [],
            categoriesName: [],
          };
          setBackendFilterValues(filterData);
          setFilterValues(filterData);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching filter data:", error);
        }
      }
    };

    if (innerChnages) {
      fetchFilterData();
    }

    return () => {
      controller.abort();
      setIsSetFilter(false);
    };
  }, [innerChnages, setBackendFilterValues, setFilterValues, setIsSetFilter]);

  // Set up create button options
  useEffect(() => {
    setCreateBtn(true);
    setCreateBtnName("Add Design");
    if (isMeltingStock) {
      setCreateBtnOption([{ option: `By Stock`, link: "/Stock", view: true, newName: "Create Melting Stock" }]);
    } else {
      setCreateBtnOption([
        { option: `By Category`, link: "/Category", view: hasAccessViewCategory, newName: "Create Order" },
        { option: `By Catalog`, link: "/Catalog", view: hasAccessViewCategory, newName: "Create Order" },
        { option: `By Collection`, link: "/Collection", view: hasAccessViewCollection, newName: "Create Order" },
        { option: `By Sets`, link: "/Sets", view: hasAccessViewSets, newName: "Create Order" },
        { option: `By Stock`, link: "/Stock", view: hasAccessViewStocks, newName: "Create Order" },
      ]);
    }
    return () => {
      setCreateBtn(false);
      setCreateBtnName("");
      setCreateBtnOption([]);
      setBackendFilterValues({});
      setClientDetailsComp(false);
    };
  }, [
    location,
    hasAccessViewCategory,
    hasAccessViewCollection,
    hasAccessViewSets,
    hasAccessViewStocks,
    setCreateBtn,
    setCreateBtnName,
    setCreateBtnOption,
    setBackendFilterValues,
    setClientDetailsComp,
  ]);

  // Set client details data
  useEffect(() => {
    setClientDetailsComp(true);

    const OrderDetailsData = [
      {
        icon: <ColorDiamond />,
        label: "Diamond Weight",
        value: isMeltingStock ? (
          <span style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "16px" }}>
            {`${diamondWeight} Ct`}
            <InfoIcon style={{ width: "18px", height: "18px", cursor: "pointer" }} onClick={() => setDiamondModalOpen(true)} />
          </span>
        ) : (
          `${diamondWeight} Ct`
        ),
        fetchData: isMeltingStock
          ? async () => {
              const breakdown = getDiamondQualityBreakdown();
              const info = breakdown.reduce((acc, { key, value }) => {
                acc[key] = String(value).padStart(2, "0");
                return acc;
              }, {});
              return { data: { info } };
            }
          : undefined,
        title: "Diamond Quality",
        titleIcon: <BlueDiamond style={{ width: 22, height: 22 }} />,
      },
      {
        icon: <ColorGold />,
        label: "Gold Weight",
        value: isMeltingStock ? (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontSize: "16px",
            }}
          >
            {`${goldWeight} g`}
            <InfoIcon style={{ width: "18px", height: "18px", cursor: "pointer" }} onClick={() => setGoldModalOpen(true)} />
          </span>
        ) : (
          `${goldWeight} g`
        ),
        fetchData: isMeltingStock
          ? async () => {
              const breakdown = getGoldQualityBreakdown();
              const info = breakdown.reduce((acc, { key, value }) => {
                acc[key] = String(value).padStart(2, "0");
                return acc;
              }, {});
              return { data: { info } };
            }
          : undefined,
        title: "Gold Purity",
        titleIcon: <ColorGold style={{ width: 24, height: 24 }} />,
      },
      {
        icon: <ColorRings />,
        label: "Design Count",
        value: cartData.length,
      },
      {
        icon: <ColorCategory />,
        label: "Categories",
        value: Array.isArray(categorySections) ? categorySections.length : 0,
      },
    ];

    setClientDetails(OrderDetailsData);

    // Update cart count
    setCountCart(cartData.length);

    return () => {
      setClientDetailsComp(false);
    };
  }, [cartData, categorySections, getDiamondQualityBreakdown, getGoldQualityBreakdown, isMeltingStock, diamondWeight, goldWeight, setClientDetails, setClientDetailsComp, setCountCart]);

  const categoryNames = useMemo(() => {
    return categorySections.map(([categoryName]) => categoryName);
  }, [categorySections]);

  const { allSizeData, isLoading: isSizesLoading, isFetch } = useCategorySizes(categoryNames);

  const showSetIcon = useCallback(
    (relevantSetDesigns) => {
      try {
        const cartStyleNumbers = cartData.map((item) => item.styleNumber?.toString());
        return relevantSetDesigns.some((styleNum) => cartStyleNumbers.includes(styleNum?.toString()));
      } catch (error) {
        console.error("Error checking set icon visibility:", error);
        return false;
      }
    },
    [cartData]
  );

  if (categorySections && categorySections.length === 0) {
    return (
      <div
        style={{
          width: "100%",
          height: "70vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {/* <CircularProgress color="success" /> */}
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div
        style={{
          flex: 1,
          flexGrow: "initial",
          marginBottom: "2.4vw",
        }}
      >
        {categorySections && categorySections.length > 0 ? (
          <>
            {categorySections.map((item, index) => {
              const categoriesName = item[0];
              return (
                <Grid
                  container
                  direction="row"
                  justifyContent="flex-start"
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                  }}
                  key={`category-${index}`}
                >
                  <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
                    <div
                      style={{
                        fontFamily: "Poppins",
                        display: "flex",
                        alignItems: "center",
                        fontSize: ".9vw",
                        color: "white",
                        fontWeight: "500",
                        height: "3.5vw",
                      }}
                    >
                      <span
                        style={{ padding: ".3vw", cursor: "pointer", display: "flex", gap: ".5vw", alignItems: "center" }}
                        onClick={() => handleToggleCategory(item[0])}
                        role="button"
                        tabIndex={0}
                        onKeyPress={(e) => e.key === "Enter" && handleToggleCategory(item[0])}
                        aria-expanded={!collapsed[item[0]]}
                      >
                        <DownArrow
                          style={{
                            transform: collapsed[item[0]] ? "rotate(-90deg)" : "rotate(0deg)",
                            transition: "transform 0.3s ease",
                          }}
                        />
                        <span style={{ fontWeight: "700" }}>
                          {categoriesName} - {item[1].length}
                        </span>
                        <span style={{ color: "#595959" }}>
                          ({item[1].length} {item[1].length === 1 ? "Article" : "Articles"})
                        </span>
                        {!(isSizesLoading || isFetch) &&
                          (() => {
                            const categoryName = item[0];
                            const sizeList = allSizeData[categoryName] || [];
                            if (sizeList.length > 0) {
                              const notSizedCount = item[1]?.filter((art) => art.sizeAvailable && (!art.itemSize || art.itemSize === "Select" || art.itemSize === "Select Size"))?.length;
                              return notSizedCount > 0 ? (
                                <span
                                  style={{
                                    border: "1px solid #F23D3D",
                                    padding: "0.3vw 0.5vw",
                                    color: "#F23D3D",
                                    borderRadius: "0.5vw",
                                    fontSize: "0.7vw",
                                    fontWeight: "700",
                                    fontFamily: "Karla",
                                    backgroundColor: "rgba(242, 61, 61, 0.03)",
                                  }}
                                >
                                  {notSizedCount} {notSizedCount === 1 ? "Article" : "Articles"}
                                </span>
                              ) : null;
                            }
                            return null;
                          })()}
                      </span>
                    </div>
                    <Collapse in={!collapsed[item[0]]} timeout={300} unmountOnExit={false} easing="ease-in-out">
                      <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        sx={{
                          display: "flex",

                          justifyContent: "flex-start",
                          gap: !isMeltingStock ? "1.8vw" : "0px",
                          rowGap: !isMeltingStock ? "1.8vw" : "0px",
                          marginBottom: "20px",
                        }}
                        //className="Grid-padding"
                      >
                        {item[1]?.map((cart, index) => {
                          const cartProduct = isMeltingStock ? cart : transformDataForCreateOrderPage(cart);
                          return (
                            <Grid key={cartProduct.cartItemId || `${cartProduct.styleNumber}-${index}`} item className={isMeltingStock ? "stock" : ""}>
                              {isMeltingStock ? (
                                <StockCard data={cartProduct} isBtn={false} removeCart={() => handlemodalOpen(cartProduct)} allData={filteredData} disableClick={isMeltingStock} />
                              ) : (
                                <LongCard
                                  data={cartProduct}
                                  setIcon={showSetIcon(cartProduct.relavantSetsDesign) ? cartProduct.relavantSetsDesign : []}
                                  RemoveDesign={handlemodalOpen}
                                  invalidStyleNumbers={invalidStyleNumbers}
                                  setInvalidStyleNumbers={setInvalidStyleNumbers}
                                  sizeData={isClientDesign(cartProduct) ? NO_SIZES : allSizeData[cartProduct.categoryName]}
                                  isLoading={isSizesLoading || isFetch}
                                  cartItemId={cartProduct.cartItemId}
                                />
                              )}
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Collapse>
                  </div>
                  <Divider
                    sx={{
                      border: "0.15vw solid",
                      borderColor: "#0D0D0D",
                      width: "100%",
                    }}
                  />
                </Grid>
              );
            })}
          </>
        ) : (
          <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>No items in cart</div>
        )}
      </div>

      {!(isSizesLoading || isFetch) && !isMeltingStock && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            width: "100vw",
            backgroundColor: isAddQuoteFlow ? "#50C878" : showBtn ? "grey" : "#50C878",
            height: "2.4vw",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontSize: ".9vw",
            fontWeight: "600",
            fontFamily: "Poppins",
            color: "#000000",
            cursor: updateLoading ? "not-allowed" : "pointer",
            letterSpacing: "1px",
            zIndex: 1000,
            opacity: updateLoading ? 0.7 : 1,
          }}
          onClick={isAddQuoteFlow ? handleUpdateClick : handleProceedClickWrapper}
        >
          {isAddQuoteFlow ? (updateLoading ? "Updating..." : "Update") : "Proceed"}
        </div>
      )}

      <RemoveModal open={removeProductModal} setOpen={setRemoveProductModal} btnInfo={btnInfo} heading={`Do you want to Remove ${productToRemove?.styleNumber} Design?`} />

      <DiamondQualityModal open={diamondModalOpen} handleClose={() => setDiamondModalOpen(false)} data={diamondQualityData} />

      <GoldPurityModal open={goldModalOpen} handleClose={() => setGoldModalOpen(false)} data={goldQualityData} />
    </div>
  );
}

export default CartOrder;
